import type { Member, Recipient, StaffAction } from "@payezy/api-client/admin";
import { listActionTypesOptions, requestActionMutation } from "@payezy/api-client/admin/query";
import { Alert, Button, Dialog, Field, Input, Select, Textarea, toast } from "@payezy/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { showError } from "../lib/notify";

type Props = {
  type: string;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  orgId?: string;
  userId?: string;
  members?: Member[];
  recipients?: Recipient[];
  eventId?: string;
  defaults?: Record<string, string>;
  onDone?: (a: StaffAction) => void;
};

const statusOptions: Record<string, string[]> = {
  "org.set_status": ["restricted", "suspended", "active", "closed"],
  "user.set_status": ["suspended", "active"],
  "counterparty.set_screening": ["review", "blocked", "clear"],
};

/** Requests an act-on-behalf action. The reason is mandatory and lands in the audit log. */
export function ActionDialog({ type, trigger, open: controlled, onOpenChange, orgId, userId, members, recipients, eventId, defaults, onDone }: Props) {
  const qc = useQueryClient();
  const types = useQuery(listActionTypesOptions());
  const def = types.data?.items.find((t) => t.type === type);
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = (o: boolean) => (onOpenChange ?? setUncontrolled)(o);
  const [reason, setReason] = useState("");
  const [user, setUser] = useState(userId ?? "");
  const [fields, setFields] = useState<Record<string, string>>({ ...defaults });
  const set = (k: string, v: string) => setFields((f) => ({ ...f, [k]: v }));

  const request = useMutation({
    ...requestActionMutation(),
    onSuccess: (a) => {
      setOpen(false);
      setReason("");
      if (a.status === "pending_approval") toast.success("Request sent for a second approver.");
      else if (a.status === "executed") toast.success("Done.");
      else if (a.status === "failed") toast.error(a.error || "The action failed.");
      void qc.invalidateQueries();
      onDone?.(a);
    },
    onError: showError,
  });

  if (!def) return null;

  const payload: Record<string, unknown> = {};
  switch (type) {
    case "org.set_status":
    case "user.set_status":
      payload.status = fields.status ?? statusOptions[type]![0];
      break;
    case "org.set_limits":
      payload.single = fields.single ?? "";
      payload.daily = fields.daily ?? "";
      break;
    case "counterparty.set_screening":
      payload.counterparty_id = fields.counterparty_id ?? recipients?.[0]?.id ?? "";
      payload.status = fields.status ?? statusOptions[type]![0];
      break;
    case "webhook.replay":
      payload.event_id = eventId ?? fields.event_id ?? "";
      break;
  }

  const submit = () =>
    request.mutate({
      body: { type, reason, org_id: def.needs_org ? orgId : undefined, target_user_id: def.needs_user ? user || undefined : undefined, payload },
    });

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={def.title}
      description={def.description}
      footer={
        <>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button loading={request.isPending} disabled={reason.trim().length < 10 || (def.needs_user && !user)} onClick={submit}>
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {def.needs_user && !userId && (
          <Field label="User">
            <Select value={user} onChange={(e) => setUser(e.target.value)}>
              <option value="">Choose a member…</option>
              {members?.filter((m) => m.status === "active").map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role})</option>
              ))}
            </Select>
          </Field>
        )}
        {statusOptions[type] && type !== "counterparty.set_screening" && (
          <Field label="New status">
            <Select value={String(payload.status)} onChange={(e) => set("status", e.target.value)}>
              {statusOptions[type]!.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
        )}
        {type === "org.set_limits" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Single payment (USD)"><Input inputMode="decimal" value={fields.single ?? ""} onChange={(e) => set("single", e.target.value)} placeholder="25000.00" /></Field>
            <Field label="Daily total (USD)"><Input inputMode="decimal" value={fields.daily ?? ""} onChange={(e) => set("daily", e.target.value)} placeholder="100000.00" /></Field>
          </div>
        )}
        {type === "counterparty.set_screening" && (
          <>
            <Field label="Recipient">
              <Select value={String(payload.counterparty_id)} onChange={(e) => set("counterparty_id", e.target.value)}>
                {recipients?.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.screening}</option>)}
              </Select>
            </Field>
            <Field label="Screening decision">
              <Select value={String(payload.status)} onChange={(e) => set("status", e.target.value)}>
                {statusOptions[type]!.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
          </>
        )}
        {type === "webhook.replay" && !eventId && (
          <Field label="Partner event id"><Input value={fields.event_id ?? ""} onChange={(e) => set("event_id", e.target.value)} /></Field>
        )}
        {type === "impersonation.write" && (
          <Alert tone="warning" title="Write access acts as the customer">
            Every request is attributed to you in the customer's audit log. A second staff member must approve, and the session expires automatically.
          </Alert>
        )}
        <Field label="Reason" hint="Include the ticket number and what you're doing. Customers' admins can see staff actions in their activity log.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="SUP-1234: customer locked out after losing their phone" />
        </Field>
      </div>
    </Dialog>
  );
}
